import { useState, useEffect, useRef, type ComponentProps } from "react";
import type { ReactNode } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { PublicClientApplication } from "@azure/msal-browser";
import {
  LibraryProvider,
  ProtectedRoute,
  NotFound,
  LoadingSpinner,
  ErrorBoundary,
  createMsalConfig,
  useAuth,
} from "@cloudaccelerator-ui/react-library";
import { appConfigService } from "@/services/appConfigService";
import { apiService } from "@/services/apiService";
import type { IAppEnvironment } from "@/types/configTypes";
import { AppLayout } from "@/components/layout/AppLayout/AppLayout";
import { HomePage } from "@/pages/HomePage/HomePage";
import { LocalePickerPage } from "@/pages/LocalePickerPage/LocalePickerPage";

/**
 * Configures the apiService with the auth token provider once on mount.
 */
function ApiServiceInitializer({ children }: { children: ReactNode }): ReactNode {
  const { getAccessToken } = useAuth();
  const configured = useRef(false);

  useEffect(() => {
    if (!configured.current) {
      apiService.configure(getAccessToken);
      configured.current = true;
    }
  }, [getAccessToken]);

  return children;
}

export function App(): React.ReactElement {
  type LibraryMsalInstance = ComponentProps<typeof LibraryProvider>["msalInstance"];
  const [msalInstance, setMsalInstance] = useState<LibraryMsalInstance | null>(null);
  const [scopes, setScopes] = useState<string[]>([]);
  const [envConfig, setEnvConfig] = useState<IAppEnvironment | null>(null);

  useEffect(() => {
    appConfigService.getAppEnvironmentData()
      .then(async (config) => {
        const msalConfig = createMsalConfig({
          auth: {
            clientId: config.aadClientId,
            authority: `https://login.microsoftonline.com/${config.aadTenantId}/`,
            redirectUri: window.location.origin,
            postLogoutRedirectUri: window.location.origin,
          },
          cache: {
            cacheLocation: "localStorage",
          },
        });

        const instance = new PublicClientApplication(msalConfig);
        await instance.initialize();

        setScopes(config.scopes);
        setEnvConfig(config);
        setMsalInstance(instance as unknown as LibraryMsalInstance);
      })
      .catch(console.error);
  }, []);

  if (!msalInstance || !envConfig) {
    return <LoadingSpinner label="Loading authentication..." fullPage />;
  }

  return (
    <LibraryProvider
      msalInstance={msalInstance}
      interactionMode="redirect"
      defaultScopes={scopes}
      theme={envConfig.enableBrowserTheme ? undefined : "light"}
    >
      <ErrorBoundary>
        <ApiServiceInitializer>
          <BrowserRouter>
            <Routes>
              <Route element={
                <ProtectedRoute autoLogin>
                  <AppLayout
                    uhfApiUrl={envConfig.uhfApiUrl}
                    envName={envConfig.env}
                    navbarColor={envConfig.uhfNavbarColor}
                  />
                </ProtectedRoute>
              }>
                <Route path="/" element={<HomePage />} />
                <Route path="/locale" element={<LocalePickerPage />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </ApiServiceInitializer>
      </ErrorBoundary>
    </LibraryProvider>
  );
}
